/**
 * Blog post content.
 *
 * WHY THIS SHAPE
 * --------------
 * Posts are structured data, not Markdown and not JSX. The reason is a trap in
 * this codebase specifically: index.css carries a universal
 * `* { font-family: <body face> }` that outranks any family set on an
 * ancestor (see the comment at components/ui/primitives.jsx). A raw <p> or <li>
 * inside a styled wrapper therefore renders silently in the DISPLAY face, and
 * nothing catches it — not the build, not the prerender assertions.
 *
 * Because BlogPost.js maps every node type through Display/Body/Mono, that
 * mistake is unrepresentable here rather than merely discouraged.
 *
 * It also keeps content diffing separately from UI, the same reason
 * data/conversions.js exists apart from Convert.js.
 *
 * ONE FILE PER POST
 * -----------------
 * scripts/generate-sitemap.mjs derives each URL's <lastmod> from the git commit
 * date of its source files. One file per post means editing one article marks
 * exactly that URL as changed, instead of all 25 — which is the whole point of
 * deriving lastmod from git rather than stamping the build date.
 *
 * POST SHAPE
 *   slug          URL segment; must match the filename
 *   title         <h1> and <title>. Written for the search result, not the page
 *   description   meta description + hub card copy. One sentence, specific
 *   keywords[]    real queries this targets; also feeds related-post ranking
 *   cluster       one of BLOG_CLUSTERS[].id
 *   pillar        true for the cluster's hub article (sorts first, ranks higher)
 *   published     ISO date. Never back-dated
 *   updated       ISO date, optional
 *   hero          { src, alt } — alt is real description, not the title again
 *   lede          opening paragraph, rendered larger. Plain string
 *   sections[]    the article body (see NODE TYPES)
 *   faq[]         [{q, a}] — omit entirely unless the questions are real ones
 *   related[]     slugs to force into the related list, overriding scoring
 *
 * NODE TYPES (sections[].body[])
 *   { type: 'para',  text }               a paragraph
 *   { type: 'list',  items[], ordered? }  a bulleted or numbered list
 *   { type: 'sub',   text }               an <h3> inside a section
 *   { type: 'quote', text, cite? }        a pull quote
 *   { type: 'callout', title?, text }     a boxed aside
 *   { type: 'example', bad, good, note? } a before/after pair — the highest
 *                                         value node type, and the one that
 *                                         makes a page quotable
 *
 * Inline links inside `text` use [label](/path). The renderer resolves them; it
 * does not accept raw HTML.
 *
 * HOUSE RULES FOR THE PROSE
 * -------------------------
 * See SEO.md. The short version: say something specific and checkable, or say
 * nothing. No invented statistics, no fabricated case studies, no "in today's
 * fast-paced digital landscape". A claim about how the platform behaves must be
 * one we would defend; where something is genuinely uncertain, say so plainly —
 * openly stated uncertainty reads as more authoritative than false precision,
 * not less.
 */

import facelessChannelGuide from './faceless-youtube-channel-guide.js';
import facelessFormats from './faceless-youtube-video-formats.js';
import firstTwentyVideos from './first-20-youtube-videos.js';
import postingSchedule from './youtube-posting-schedule.js';
import channelFailingOrEarly from './youtube-channel-not-growing.js';

import nichesPillar from './faceless-youtube-niches.js';
import nicheEvaluation from './how-to-evaluate-a-youtube-niche.js';
import nichesToAvoid from './youtube-niches-to-avoid.js';
import narrowNiche from './narrow-youtube-niche.js';
import rpmHonestly from './youtube-rpm-by-niche.js';

import topicPillar from './youtube-topic-research.js';
import topicsWithQuestions from './youtube-video-ideas-that-work.js';
import competitorGapAnalysis from './youtube-competitor-research.js';
import titleAndThumbnail from './youtube-title-and-thumbnail.js';
import hooksThatHold from './youtube-hook-writing.js';
import evergreenVsTrending from './evergreen-vs-trending-youtube.js';
import searchVsBrowse from './youtube-search-vs-browse-traffic.js';

import scriptPillar from './youtube-script-writing.js';
import scriptStructure from './youtube-video-script-structure.js';
import openLoops from './youtube-retention-open-loops.js';
import writingForTheEar from './writing-a-script-for-narration.js';
import pacingIsEditing from './script-pacing-and-shot-length.js';
import specificityVisuals from './specific-script-better-visuals.js';
import factsYouAreUnsure from './handling-uncertain-facts-in-scripts.js';
import scriptLength from './how-long-should-a-youtube-script-be.js';

// Clusters exist so the hub can group posts and so related-post ranking has a
// notion of topical adjacency. Each has exactly one pillar.
export const BLOG_CLUSTERS = [
  {
    id: 'strategy',
    label: 'Channel strategy',
    blurb:
      'What a faceless channel actually is, what to publish, and how to read your own numbers before you have enough of them to read.',
  },
  {
    id: 'niches',
    label: 'Niches',
    blurb:
      'Choosing something to be about — and being honest about demand, competition and what each niche pays.',
  },
  {
    id: 'topics',
    label: 'Topics and packaging',
    blurb:
      'Finding videos worth making, then giving them a title and thumbnail that earn the click they deserve.',
  },
  {
    id: 'scripting',
    label: 'Scripting',
    blurb:
      'The craft part. Structure, tension, specificity, and writing words that are going to be spoken rather than read.',
  },
];

const ALL = [
  facelessChannelGuide, facelessFormats, firstTwentyVideos, postingSchedule,
  channelFailingOrEarly,

  nichesPillar, nicheEvaluation, nichesToAvoid, narrowNiche, rpmHonestly,

  topicPillar, topicsWithQuestions, competitorGapAnalysis, titleAndThumbnail,
  hooksThatHold, evergreenVsTrending, searchVsBrowse,

  scriptPillar, scriptStructure, openLoops, writingForTheEar, pacingIsEditing,
  specificityVisuals, factsYouAreUnsure, scriptLength,
];

export const BLOG_POSTS = ALL.reduce((acc, post) => {
  if (acc[post.slug]) {
    throw new Error(`Duplicate blog slug: ${post.slug}`);
  }
  acc[post.slug] = post;
  return acc;
}, {});
